import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { MainLayout } from '@/components/layout/MainLayout';
import { SpacesPage } from '@/pages/SpacesPage';
import { SpaceDetailPage } from '@/pages/SpaceDetailPage';
import { ObjectDetailPage } from '@/pages/ObjectDetailPage';
import { SearchPage } from '@/pages/SearchPage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { VerifyEmailPage } from '@/pages/VerifyEmailPage';
import { AuthCallbackPage } from '@/pages/AuthCallbackPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { SettingsPage } from '@/pages/SettingsPage';
import { AdminUsersPage } from '@/pages/AdminUsersPage';
import { SpaceSettingsPage } from '@/pages/SpaceSettingsPage';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// Lazy-loaded visualization pages
const GraphPage = lazy(() => import('@/pages/GraphPage').then((m) => ({ default: m.GraphPage })));
const ImpactPage = lazy(() => import('@/pages/ImpactPage').then((m) => ({ default: m.ImpactPage })));
const CapabilityMapPage = lazy(() => import('@/pages/CapabilityMapPage').then((m) => ({ default: m.CapabilityMapPage })));
const MatrixPage = lazy(() => import('@/pages/MatrixPage').then((m) => ({ default: m.MatrixPage })));
const ImportPage = lazy(() => import('@/pages/ImportPage').then((m) => ({ default: m.ImportPage })));

function PageLoader() {
  return (
    <div className="flex h-96 items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function AuthLoader({ children }: { children: React.ReactNode }) {
  const hasHydrated = useAuthStore((state) => state._hasHydrated);

  if (!hasHydrated) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <AuthLoader>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        
        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Navigate to="/spaces" replace />} />
                    <Route path="/spaces" element={<SpacesPage />} />
                    <Route path="/spaces/:spaceKey" element={<SpaceDetailPage />} />
                    <Route path="/spaces/:spaceKey/settings" element={<SpaceSettingsPage />} />
                    <Route path="/spaces/:spaceKey/objects/:objectId" element={<ObjectDetailPage />} />
                    <Route path="/spaces/:spaceKey/graph" element={<GraphPage />} />
                    <Route path="/spaces/:spaceKey/impact" element={<ImpactPage />} />
                    <Route path="/spaces/:spaceKey/capabilities" element={<CapabilityMapPage />} />
                    <Route path="/spaces/:spaceKey/matrix" element={<MatrixPage />} />
                    <Route path="/spaces/:spaceKey/import" element={<ImportPage />} />
                    <Route path="/graph" element={<GraphPage />} />
                    <Route path="/impact" element={<ImpactPage />} />
                    <Route path="/search" element={<SearchPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/admin/users" element={<AdminUsersPage />} />
                  </Routes>
                </Suspense>
              </MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthLoader>
  );
}
